import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";
import TaskSkeleton from "./TaskSkeleton";
import {
    downloadVouchersCsv,
    downloadVouchersPdf,
    printVouchers,
    voucherLabel,
} from "../utils/expenseVoucherExport";
import ExpenseSearchSelect, { mapToSelectOptions } from "./ExpenseSearchSelect";
import "./ExpenseModule.css";
import {
    buildFinancialYearOptions,
    formatDisplayDate,
    formatFinancialYear,
    getFinancialYearStart,
    toDateInputValue,
} from "../utils/financialYear";
import { getCompanyLogoUrl } from "../utils/imageUtils";

const NAVY = "#1a237e";
const NAVY_GRADIENT = "linear-gradient(135deg, #1a237e 0%, #283593 100%)";

const fmt = (v) => Math.round(parseFloat(v || 0)).toLocaleString("en-IN");

const fmtDate = (d) => formatDisplayDate(d);

const voucherNumberSortKey = (voucher) => {
    const label = String(voucher?.voucher_number || voucherLabel(voucher) || "");
    const parts = label.split("-");
    const seq = parseInt(parts[parts.length - 1], 10);
    const yearPart = parseInt(parts.length > 1 ? parts[1] : "", 10);
    return {
        year: Number.isFinite(yearPart) ? yearPart : 0,
        seq: Number.isFinite(seq) ? seq : 0,
        label,
    };
};

const compareVoucherNumber = (a, b) => {
    const ka = voucherNumberSortKey(a);
    const kb = voucherNumberSortKey(b);
    if (ka.year !== kb.year) return ka.year - kb.year;
    if (ka.seq !== kb.seq) return ka.seq - kb.seq;
    return ka.label.localeCompare(kb.label, undefined, { numeric: true, sensitivity: "base" });
};


export default function ExpenseItemList() {
    const { http, user } = AuthUser();
    const [heads, setHeads] = useState([]);
    const [summary, setSummary] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [year, setYear] = useState(getFinancialYearStart());
    const [headId, setHeadId] = useState("");
    const [vendorId, setVendorId] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [sortBy, setSortBy] = useState("voucher_number"); // voucher_number | date
    const [sortDir, setSortDir] = useState("desc"); // asc | desc
    const [viewVoucher, setViewVoucher] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [viewMode, setViewMode] = useState("list");
    const [companyName, setCompanyName] = useState("");
    const [companyLogoUrl, setCompanyLogoUrl] = useState("");
    const [companyAddress, setCompanyAddress] = useState("");
    const [editItemId, setEditItemId] = useState(null);
    const [editItemForm, setEditItemForm] = useState({ quantity: "", price: "", gst_percent: "", description: "" });
    const [savingItem, setSavingItem] = useState(false);
    const [editVoucherDate, setEditVoucherDate] = useState("");
    const [savingDate, setSavingDate] = useState(false);
    const yearOptions = buildFinancialYearOptions();

    useEffect(() => {
        http.get(`/expense_heads?login_user=${user.id}`)
            .then((res) => { if (res.data.success) setHeads(res.data.data || []); })
            .catch(() => {});

        http.get(`/companyinfo?login_user=${user.id}`)
            .then((res) => {
                if (res.data.data?.length > 0) {
                    const company = res.data.data[0];
                    setCompanyName(company.company_name || "");
                    setCompanyAddress(company.company_address || company.address || "");
                    if (company.company_logo) {
                        setCompanyLogoUrl(getCompanyLogoUrl(company.company_logo));
                    }
                }
            })
            .catch(() => {});
    }, []);

    useEffect(() => { fetchSummary(); }, [year, headId]);

    useEffect(() => {
        setSelectedIds([]);
    }, [year, headId, vendorId, dateFrom, dateTo]);

    const unfilteredVouchers = useMemo(() => {
        if (!summary) return [];
        if (summary.vouchers?.length) return summary.vouchers;
        return (summary.months || []).flatMap((m) => m.vouchers || []);
    }, [summary]);

    const vendorOptions = useMemo(() => {
        const vendorsById = new Map();
        unfilteredVouchers.forEach((voucher) => {
            if (voucher.vendor?.id) {
                vendorsById.set(String(voucher.vendor.id), {
                    value: String(voucher.vendor.id),
                    label: voucher.vendor.name || "Unnamed vendor",
                });
            }
        });
        return Array.from(vendorsById.values()).sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
        );
    }, [unfilteredVouchers]);

    const allVouchers = useMemo(() => {
        const filtered = unfilteredVouchers.filter((voucher) => {
            const matchesVendor = !vendorId
                || String(voucher.expense_vendor_master_id || voucher.vendor?.id) === vendorId;
            const voucherDate = toDateInputValue(voucher.voucher_date);
            const matchesFrom = !dateFrom || (voucherDate && voucherDate >= dateFrom);
            const matchesTo = !dateTo || (voucherDate && voucherDate <= dateTo);
            return matchesVendor && matchesFrom && matchesTo;
        });

        const dir = sortDir === "asc" ? 1 : -1;
        return [...filtered].sort((a, b) => {
            let cmp = 0;
            if (sortBy === "date") {
                const da = toDateInputValue(a.voucher_date) || "";
                const db = toDateInputValue(b.voucher_date) || "";
                cmp = da.localeCompare(db);
                if (cmp === 0) cmp = compareVoucherNumber(a, b);
            } else {
                cmp = compareVoucherNumber(a, b);
                if (cmp === 0) {
                    const da = toDateInputValue(a.voucher_date) || "";
                    const db = toDateInputValue(b.voucher_date) || "";
                    cmp = da.localeCompare(db);
                }
            }
            return cmp * dir;
        });
    }, [unfilteredVouchers, vendorId, dateFrom, dateTo, sortBy, sortDir]);

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
            return;
        }
        setSortBy(field);
        setSortDir("desc");
    };

    const sortIcon = (field) => {
        if (sortBy !== field) return "fa-sort";
        return sortDir === "asc" ? "fa-sort-up" : "fa-sort-down";
    };

    const hasDateRangeFilter = Boolean(dateFrom || dateTo);
    const hasListFilters = Boolean(vendorId || hasDateRangeFilter);

    const clearListFilters = () => {
        setVendorId("");
        setDateFrom("");
        setDateTo("");
    };

    const selectedVouchers = useMemo(
        () => allVouchers.filter((v) => selectedIds.includes(v.id)),
        [allVouchers, selectedIds]
    );

    const fetchSummary = async () => {
        setIsLoading(true);
        try {
            let url = `/expense_vouchers_summary?login_user=${user.id}&year=${year}`;
            if (headId) url += `&head_id=${headId}`;
            const res = await http.get(url);
            if (res.data.success) {
                setSummary(res.data);
            }
        } catch (err) {
            toast.error("Error loading vouchers");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteVoucher = async (id) => {
        if (!window.confirm("Delete this voucher and all its items?")) return;
        try {
            const res = await http.delete(`/expense_voucher/${id}`);
            if (res.data.success) {
                toast.success("Voucher deleted!");
                setViewVoucher(null);
                setEditItemId(null);
                setSelectedIds((prev) => prev.filter((x) => x !== id));
                fetchSummary();
            }
        } catch (err) {
            toast.error("Error deleting voucher");
        }
    };

    const openVoucherModal = (voucher) => {
        setViewVoucher(voucher);
        setEditVoucherDate(toDateInputValue(voucher.voucher_date));
        setEditItemId(null);
        setEditItemForm({ quantity: "", price: "", gst_percent: "", description: "" });
    };

    const saveVoucherDate = async () => {
        if (!viewVoucher) return;
        if (!editVoucherDate) {
            toast.error("Please select a voucher date");
            return;
        }
        const currentDate = toDateInputValue(viewVoucher.voucher_date);
        if (editVoucherDate === currentDate) return;

        setSavingDate(true);
        try {
            const res = await http.put(`/expense_voucher/${viewVoucher.id}?login_user=${user.id}`, {
                voucher_date: editVoucherDate,
                description: viewVoucher.description || null,
            });
            if (res.data.success) {
                setViewVoucher(res.data.data);
                setEditVoucherDate(toDateInputValue(res.data.data.voucher_date) || editVoucherDate);
                fetchSummary();
                toast.success("Voucher date updated!");
            } else {
                toast.error(res.data.message || "Error updating date");
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Error updating date");
        } finally {
            setSavingDate(false);
        }
    };

    const startEditItem = (item) => {
        setEditItemId(item.id);
        setEditItemForm({
            quantity: item.quantity !== null && item.quantity !== undefined ? String(item.quantity) : "",
            price: item.price !== null && item.price !== undefined ? String(item.price) : "",
            gst_percent: item.gst_percent !== null && item.gst_percent !== undefined ? String(item.gst_percent) : "",
            description: item.description || "",
        });
    };

    const cancelEditItem = () => {
        setEditItemId(null);
        setEditItemForm({ quantity: "", price: "", gst_percent: "", description: "" });
    };

    const handleEditItemChange = (e) => {
        const { name, value } = e.target;
        setEditItemForm((prev) => ({ ...prev, [name]: value }));
    };

    const saveEditItem = async (itemId) => {
        if (!editItemForm.quantity || parseFloat(editItemForm.quantity) <= 0) {
            toast.error("Please enter valid quantity");
            return;
        }
        if (editItemForm.price === "" || parseFloat(editItemForm.price) < 0) {
            toast.error("Please enter valid price");
            return;
        }
        if (
            editItemForm.gst_percent !== ""
            && (parseFloat(editItemForm.gst_percent) < 0 || parseFloat(editItemForm.gst_percent) > 100)
        ) {
            toast.error("Please enter valid GST %");
            return;
        }

        setSavingItem(true);
        try {
            const res = await http.put(`/expense_voucher_item/${itemId}?login_user=${user.id}`, {
                quantity: parseFloat(editItemForm.quantity),
                price: parseFloat(editItemForm.price),
                gst_percent: editItemForm.gst_percent === "" ? 0 : parseFloat(editItemForm.gst_percent),
                description: editItemForm.description.trim() || null,
            });
            if (res.data.success) {
                setViewVoucher(res.data.data);
                cancelEditItem();
                fetchSummary();
                toast.success("Voucher item updated!");
            } else {
                toast.error(res.data.message || "Error updating item");
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Error updating item");
        } finally {
            setSavingItem(false);
        }
    };

    const editAmountPreview = (() => {
        const qty = parseFloat(editItemForm.quantity) || 0;
        const price = parseFloat(editItemForm.price) || 0;
        const gst = parseFloat(editItemForm.gst_percent) || 0;
        const gross = qty * price;
        return gst > 0 ? gross * (1 + gst / 100) : gross;
    })();

    const toggleSelect = (id) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const selectAllMonth = () => {
        setSelectedIds(allVouchers.map((v) => v.id));
    };

    const clearSelection = () => setSelectedIds([]);

    const runExport = async (type, vouchers) => {
        if (!vouchers?.length) {
            toast.error("No vouchers selected");
            return;
        }
        const companyOpts = { companyLogoUrl, companyAddress };
        let ok = false;
        try {
            if (type === "pdf") ok = await downloadVouchersPdf(vouchers, companyName, companyOpts);
            else if (type === "csv") ok = downloadVouchersCsv(vouchers);
            else if (type === "print") ok = printVouchers(vouchers, companyName, companyOpts);
        } catch (err) {
            console.error(err);
            ok = false;
        }
        if (ok) toast.success(type === "print" ? "Opening print..." : `${type.toUpperCase()} downloaded`);
        else toast.error("Export failed");
    };

    const ExportButtons = ({ vouchers, size = "sm", className = "" }) => {
        const disabled = !vouchers?.length;
        const btnClass = `btn btn-${size}`;
        return (
            <div className={`d-flex gap-1 ${className}`}>
                <button type="button" className={`${btnClass} btn-outline-secondary`} disabled={disabled} onClick={() => runExport("print", vouchers)} title="Print">
                    <i className="fas fa-print"></i>
                </button>
                <button type="button" className={`${btnClass} btn-outline-danger`} disabled={disabled} onClick={() => runExport("pdf", vouchers)} title="Download PDF">
                    <i className="fas fa-file-pdf"></i>
                </button>
                <button type="button" className={`${btnClass} btn-outline-success`} disabled={disabled} onClick={() => runExport("csv", vouchers)} title="Download CSV">
                    <i className="fas fa-file-csv"></i>
                </button>
            </div>
        );
    };

    const allSelected = allVouchers.length > 0 && selectedIds.length === allVouchers.length;

    const yearOptionsList = yearOptions.map((y) => ({ value: String(y), label: formatFinancialYear(y) }));
    const categoryOptions = mapToSelectOptions(heads);

    return (
        <>
            <Toaster />
            <div className="container-fluid mt-3 px-3 pb-4 expense-module">
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <div>
                        <h5 className="fw-bold mb-0">Voucher List</h5>
                        <small className="text-muted">View, print and export generated vouchers</small>
                    </div>
                    <Link to="/expense_item_add" className="btn btn-sm text-white px-3" style={{ background: NAVY_GRADIENT }}>
                        <i className="fas fa-plus me-1"></i>Voucher Create
                    </Link>
                </div>

                <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body py-2">
                        <div className="row g-2 align-items-end">
                            <div className="col-auto" style={{ minWidth: "150px" }}>
                                <label className="form-label small fw-semibold mb-1">Financial Year</label>
                                <ExpenseSearchSelect
                                    options={yearOptionsList}
                                    value={String(year)}
                                    onChange={setYear}
                                    placeholder="Search year..."
                                />
                            </div>
                            <div className="col-auto" style={{ minWidth: "180px" }}>
                                <label className="form-label small fw-semibold mb-1">Category</label>
                                <ExpenseSearchSelect
                                    options={categoryOptions}
                                    value={headId}
                                    onChange={setHeadId}
                                    placeholder="Search category..."
                                    isClearable
                                />
                            </div>
                            <div className="col-auto" style={{ minWidth: "220px" }}>
                                <label className="form-label small fw-semibold mb-1">Vendor</label>
                                <ExpenseSearchSelect
                                    options={vendorOptions}
                                    value={vendorId}
                                    onChange={setVendorId}
                                    placeholder="Search vendor..."
                                    isClearable
                                />
                            </div>
                            <div className="col-auto" style={{ minWidth: "150px" }}>
                                <label className="form-label small fw-semibold mb-1">From Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={dateFrom}
                                    max={dateTo || undefined}
                                    onChange={(e) => setDateFrom(e.target.value)}
                                />
                            </div>
                            <div className="col-auto" style={{ minWidth: "150px" }}>
                                <label className="form-label small fw-semibold mb-1">To Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={dateTo}
                                    min={dateFrom || undefined}
                                    onChange={(e) => setDateTo(e.target.value)}
                                />
                            </div>
                            <div className="col-auto" style={{ minWidth: "170px" }}>
                                <label className="form-label small fw-semibold mb-1">Sort By</label>
                                <select
                                    className="form-select form-select-sm"
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                >
                                    <option value="voucher_number">Voucher No</option>
                                    <option value="date">Date</option>
                                </select>
                            </div>
                            <div className="col-auto">
                                <label className="form-label small fw-semibold mb-1 d-block">Order</label>
                                <div className="btn-group btn-group-sm" role="group" aria-label="Sort order">
                                    <button
                                        type="button"
                                        className={`btn ${sortDir === "asc" ? "btn-primary" : "btn-outline-primary"}`}
                                        onClick={() => setSortDir("asc")}
                                        title="Ascending"
                                    >
                                        <i className="fas fa-arrow-up me-1"></i>Asc
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn ${sortDir === "desc" ? "btn-primary" : "btn-outline-primary"}`}
                                        onClick={() => setSortDir("desc")}
                                        title="Descending"
                                    >
                                        <i className="fas fa-arrow-down me-1"></i>Desc
                                    </button>
                                </div>
                            </div>
                            <div className="col-auto">
                                <button className="btn btn-sm btn-primary" onClick={fetchSummary}>
                                    <i className="fas fa-sync-alt me-1"></i>Refresh
                                </button>
                            </div>
                            {hasListFilters && (
                                <div className="col-auto">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={clearListFilters}
                                    >
                                        <i className="fas fa-times me-1"></i>Clear
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {isLoading ? <TaskSkeleton /> : summary && (
                    <>
                        {/* Budget summary / month tabs hidden — see Expense Report menu */}
                        {/*
                        <div className="row g-3 mb-3">...</div>
                        <div className="card border-0 shadow-sm mb-3">month pills</div>
                        <div className="card border-0 shadow-sm mb-3">month budget bar</div>
                        */}

                        {allVouchers.length > 0 && (
                            <div className="card border-0 shadow-sm mb-3">
                                <div className="card-body py-2 d-flex flex-wrap align-items-center justify-content-between gap-2">
                                    <div className="d-flex flex-wrap align-items-center gap-2">
                                        <div className="form-check mb-0">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="selectAllVouchers"
                                                checked={allSelected}
                                                onChange={() => (allSelected ? clearSelection() : selectAllMonth())}
                                            />
                                            <label className="form-check-label small" htmlFor="selectAllVouchers">
                                                Select all ({allVouchers.length})
                                            </label>
                                        </div>
                                        {selectedIds.length > 0 && (
                                            <span className="badge rounded-pill" style={{ background: NAVY }}>
                                                {selectedIds.length} selected
                                            </span>
                                        )}
                                    </div>
                                    <div className="d-flex flex-wrap align-items-center gap-2">
                                        <span className="small text-muted me-1">Export selected:</span>
                                        <ExportButtons vouchers={selectedVouchers} />
                                        <div className="vr d-none d-md-block mx-1"></div>
                                        <div className="btn-group btn-group-sm">
                                            <button type="button" className={`btn ${viewMode === "cards" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setViewMode("cards")}>
                                                <i className="fas fa-th-large"></i>
                                            </button>
                                            <button type="button" className={`btn ${viewMode === "list" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setViewMode("list")}>
                                                <i className="fas fa-list"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {allVouchers.length === 0 ? (
                            <div className="card border-0 shadow-sm">
                                <div className="card-body text-center py-5 text-muted">
                                    <i className="fas fa-file-invoice fa-2x mb-3 opacity-25"></i>
                                    <p className="mb-2">
                                        {hasListFilters
                                            ? "No vouchers match the selected vendor and date filters."
                                            : `No vouchers in ${summary.financial_year_label || formatFinancialYear(summary.year)}`}
                                    </p>
                                    {hasListFilters ? (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={clearListFilters}
                                        >
                                            Clear Filters
                                        </button>
                                    ) : (
                                        <Link to="/expense_item_add" className="btn btn-sm btn-outline-primary">Create Voucher</Link>
                                    )}
                                </div>
                            </div>
                        ) : viewMode === "list" ? (
                            <div className="card border-0 shadow-sm">
                                <div className="table-responsive">
                                    <table className="table table-sm table-hover mb-0 align-middle">
                                        <thead>
                                            <tr className="text-white" style={{ background: NAVY_GRADIENT }}>
                                                <th style={{ width: 36 }}></th>
                                                <th>
                                                    <button
                                                        type="button"
                                                        className="expense-sort-th"
                                                        onClick={() => toggleSort("voucher_number")}
                                                    >
                                                        Voucher No <i className={`fas ${sortIcon("voucher_number")} ms-1`}></i>
                                                    </button>
                                                </th>
                                                <th>
                                                    <button
                                                        type="button"
                                                        className="expense-sort-th"
                                                        onClick={() => toggleSort("date")}
                                                    >
                                                        Date <i className={`fas ${sortIcon("date")} ms-1`}></i>
                                                    </button>
                                                </th>
                                                <th>Vendor</th>
                                                <th className="text-center">Items</th>
                                                <th className="text-end">Total</th>
                                                <th className="text-end pe-3" style={{ minWidth: 200 }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {allVouchers.map((voucher) => {
                                                const checked = selectedIds.includes(voucher.id);
                                                return (
                                                    <tr key={voucher.id} className={checked ? "table-primary" : ""}>
                                                        <td className="ps-3">
                                                            <input type="checkbox" className="form-check-input" checked={checked} onChange={() => toggleSelect(voucher.id)} />
                                                        </td>
                                                        <td className="fw-semibold">{voucherLabel(voucher)}</td>
                                                        <td>{fmtDate(voucher.voucher_date)}</td>
                                                        <td>{voucher.vendor?.name || "—"}</td>
                                                        <td className="text-center">{(voucher.items || []).length}</td>
                                                        <td className="text-end fw-bold text-success">{fmt(voucher.total_amount)}</td>
                                                        <td className="text-end pe-3">
                                                            <div className="d-inline-flex gap-1 align-items-center flex-wrap justify-content-end">
                                                                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openVoucherModal(voucher)} title="View / Edit">
                                                                    <i className="fas fa-eye"></i>
                                                                </button>
                                                                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openVoucherModal(voucher)} title="Edit items">
                                                                    <i className="fas fa-edit"></i>
                                                                </button>
                                                                <ExportButtons vouchers={[voucher]} />
                                                                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteVoucher(voucher.id)} title="Delete">
                                                                    <i className="fas fa-trash"></i>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ) : (
                            <div className="row g-3">
                                {allVouchers.map((voucher) => {
                                    const itemCount = (voucher.items || []).length;
                                    const checked = selectedIds.includes(voucher.id);
                                    return (
                                        <div className="col-lg-6 col-xl-4" key={voucher.id}>
                                            <div className={`card border-0 shadow-sm h-100 overflow-hidden ${checked ? "ring-selected" : ""}`} style={checked ? { outline: `2px solid ${NAVY}`, outlineOffset: "2px" } : {}}>
                                                <div className="px-3 py-2 text-white d-flex justify-content-between align-items-start gap-2" style={{ background: NAVY_GRADIENT }}>
                                                    <div className="d-flex align-items-start gap-2 min-w-0">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input mt-1 flex-shrink-0"
                                                            checked={checked}
                                                            onChange={() => toggleSelect(voucher.id)}
                                                            style={{ cursor: "pointer" }}
                                                        />
                                                        <div className="min-w-0">
                                                            <div className="fw-bold text-truncate" style={{ fontSize: "0.95rem" }}>
                                                                {voucherLabel(voucher)}
                                                            </div>
                                                            <div className="small opacity-75">
                                                                <i className="far fa-calendar-alt me-1"></i>{fmtDate(voucher.voucher_date)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="text-end flex-shrink-0">
                                                        <div className="fw-bold">{fmt(voucher.total_amount)}</div>
                                                        <div className="small opacity-75">{itemCount} item{itemCount !== 1 ? "s" : ""}</div>
                                                    </div>
                                                </div>
                                                <div className="card-body py-3">
                                                    <div className="d-flex align-items-start gap-2 mb-2">
                                                        <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 36, height: 36, background: "#e8eaf6", color: NAVY }}>
                                                            <i className="fas fa-store"></i>
                                                        </div>
                                                        <div className="min-w-0">
                                                            <div className="fw-semibold text-truncate">{voucher.vendor?.name || "—"}</div>
                                                            {voucher.description && (
                                                                <div className="small text-muted text-truncate" title={voucher.description}>
                                                                    <i className="far fa-sticky-note me-1"></i>{voucher.description}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="border rounded bg-light px-2 py-1 mb-3" style={{ fontSize: "0.78rem" }}>
                                                        {(voucher.items || []).slice(0, 3).map((item) => (
                                                            <div key={item.id} className="d-flex justify-content-between text-muted py-1 border-bottom border-white">
                                                                <span className="text-truncate me-2">{item.item_name}</span>
                                                                <span className="flex-shrink-0 fw-semibold text-dark">{fmt(item.amount)}</span>
                                                            </div>
                                                        ))}
                                                        {itemCount > 3 && (
                                                            <div className="text-center text-muted py-1">+{itemCount - 3} more</div>
                                                        )}
                                                    </div>

                                                    <div className="d-flex gap-2 flex-wrap">
                                                        <button type="button" className="btn btn-sm btn-primary" onClick={() => openVoucherModal(voucher)}>
                                                            <i className="fas fa-eye me-1"></i>View
                                                        </button>
                                                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openVoucherModal(voucher)} title="Edit items">
                                                            <i className="fas fa-edit me-1"></i>Edit
                                                        </button>
                                                        <ExportButtons vouchers={[voucher]} />
                                                        <button type="button" className="btn btn-sm btn-outline-danger ms-auto" onClick={() => handleDeleteVoucher(voucher.id)} title="Delete">
                                                            <i className="fas fa-trash"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>

            {viewVoucher && (
                <div className="modal d-block expense-module" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                        <div className="modal-content border-0 shadow">
                            <div className="modal-header py-2 text-white" style={{ background: NAVY_GRADIENT }}>
                                <div>
                                    <h6 className="modal-title mb-0 fw-bold">{voucherLabel(viewVoucher)}</h6>
                                    <small className="opacity-75">{viewVoucher.vendor?.name || "—"}</small>
                                </div>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => {
                                        setViewVoucher(null);
                                        cancelEditItem();
                                    }}
                                ></button>
                            </div>
                            <div className="modal-body p-0">
                                <div className="px-3 py-2 border-bottom bg-light">
                                    <div className="row g-2 align-items-end">
                                        <div className="col-md-4">
                                            <label className="form-label small fw-semibold mb-1">Voucher Date</label>
                                            <div className="d-flex gap-2">
                                                <input
                                                    type="date"
                                                    className="form-control form-control-sm"
                                                    value={editVoucherDate}
                                                    onChange={(e) => setEditVoucherDate(e.target.value)}
                                                    disabled={savingDate}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-primary px-3"
                                                    onClick={saveVoucherDate}
                                                    disabled={
                                                        savingDate
                                                        || !editVoucherDate
                                                        || editVoucherDate === toDateInputValue(viewVoucher.voucher_date)
                                                    }
                                                >
                                                    {savingDate ? (
                                                        <span className="spinner-border spinner-border-sm"></span>
                                                    ) : (
                                                        <><i className="fas fa-save me-1"></i>Update</>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                        <div className="col-md-8">
                                            <div className="small text-muted pb-1">
                                                Current: <strong>{fmtDate(viewVoucher.voucher_date)}</strong>
                                                {viewVoucher.description ? <> &middot; Note: {viewVoucher.description}</> : null}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-sm table-hover mb-0 align-middle">
                                        <thead>
                                            <tr style={{ background: "#e8eaf6", color: NAVY }}>
                                                <th className="ps-3" style={{ width: "40px" }}>#</th>
                                                <th>Category</th>
                                                <th>Item</th>
                                                <th className="text-end">Qty</th>
                                                <th className="text-end">Price</th>
                                                <th className="text-end">GST %</th>
                                                <th className="text-end">Amount</th>
                                                <th>Note</th>
                                                <th className="text-end pe-3" style={{ width: 100 }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(viewVoucher.items || []).map((item, idx) => {
                                                const isEditing = editItemId === item.id;
                                                return (
                                                    <tr key={item.id} className={isEditing ? "table-warning" : ""}>
                                                        <td className="ps-3 text-muted">{idx + 1}</td>
                                                        <td>
                                                            <span className="badge rounded-pill" style={{ background: "#e8eaf6", color: NAVY, fontWeight: 500 }}>
                                                                {item.head?.name || "—"}
                                                            </span>
                                                        </td>
                                                        <td className="fw-semibold">{item.item_name}</td>
                                                        {isEditing ? (
                                                            <>
                                                                <td>
                                                                    <input
                                                                        type="number"
                                                                        step="0.01"
                                                                        min="0.01"
                                                                        name="quantity"
                                                                        className="form-control form-control-sm text-end"
                                                                        value={editItemForm.quantity}
                                                                        onChange={handleEditItemChange}
                                                                        disabled={savingItem}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="number"
                                                                        step="0.01"
                                                                        min="0"
                                                                        name="price"
                                                                        className="form-control form-control-sm text-end"
                                                                        value={editItemForm.price}
                                                                        onChange={handleEditItemChange}
                                                                        disabled={savingItem}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="number"
                                                                        step="0.01"
                                                                        min="0"
                                                                        max="100"
                                                                        name="gst_percent"
                                                                        className="form-control form-control-sm text-end"
                                                                        value={editItemForm.gst_percent}
                                                                        onChange={handleEditItemChange}
                                                                        disabled={savingItem}
                                                                    />
                                                                </td>
                                                                <td className="text-end fw-bold">{fmt(editAmountPreview)}</td>
                                                                <td>
                                                                    <input
                                                                        type="text"
                                                                        name="description"
                                                                        className="form-control form-control-sm"
                                                                        value={editItemForm.description}
                                                                        onChange={handleEditItemChange}
                                                                        disabled={savingItem}
                                                                        placeholder="Optional"
                                                                    />
                                                                </td>
                                                                <td className="pe-3 text-end">
                                                                    <div className="d-flex gap-1 justify-content-end">
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-success px-2"
                                                                            onClick={() => saveEditItem(item.id)}
                                                                            disabled={savingItem}
                                                                            title="Save"
                                                                        >
                                                                            {savingItem ? (
                                                                                <span className="spinner-border spinner-border-sm"></span>
                                                                            ) : (
                                                                                <i className="fas fa-check"></i>
                                                                            )}
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-secondary px-2"
                                                                            onClick={cancelEditItem}
                                                                            disabled={savingItem}
                                                                            title="Cancel"
                                                                        >
                                                                            <i className="fas fa-times"></i>
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <td className="text-end">{item.quantity}</td>
                                                                <td className="text-end">{fmt(item.price)}</td>
                                                                <td className="text-end">{fmt(item.gst_percent)}</td>
                                                                <td className="text-end fw-bold">{fmt(item.amount)}</td>
                                                                <td className="text-muted small">{item.description || "—"}</td>
                                                                <td className="pe-3 text-end">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-primary px-2"
                                                                        onClick={() => startEditItem(item)}
                                                                        disabled={savingItem || !!editItemId}
                                                                        title="Edit item"
                                                                    >
                                                                        <i className="fas fa-edit"></i>
                                                                    </button>
                                                                </td>
                                                            </>
                                                        )}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                        <tfoot>
                                            <tr style={{ background: "#fff8e1" }}>
                                                <td colSpan={6} className="text-end fw-bold ps-3 py-2">Voucher Total</td>
                                                <td className="text-end fw-bold py-2" style={{ color: "#2e7d32", fontSize: "1.05rem" }}>{fmt(viewVoucher.total_amount)}</td>
                                                <td colSpan={2} className="pe-3"></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                            <div className="modal-footer py-2 d-flex flex-wrap justify-content-between gap-2">
                                <ExportButtons vouchers={[viewVoucher]} />
                                <div className="d-flex gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-secondary"
                                        onClick={() => {
                                            setViewVoucher(null);
                                            cancelEditItem();
                                        }}
                                    >
                                        Close
                                    </button>
                                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteVoucher(viewVoucher.id)}>
                                        <i className="fas fa-trash me-1"></i>Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
